import { useState, useEffect, useRef, MouseEvent } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Select,
  FormControl,
  TextField,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import {
  GridRowSelectionModel,
  GridApiPremium,
} from "@mui/x-data-grid-premium";

import dvdlocator from "../../../apis/dvdlocator";
import _ from "lodash";
import { directiveBuilder } from "./buttonHandlers/directiveBuilder/directiveBuilder";
import {
  calcSelectedDtbs,
  summarizeDtbRun,
} from "./buttonHandlers/calcSelectedDtbs";
import { ModalSwitchState } from "./PricetoolModalSwitch";
import { BulkStringOperationConfig } from "../../../components/datagrid/types";

interface InventoryTableButtonsPanelProps {
  rowSelectionModel: GridRowSelectionModel;
  setSnackbar: (snackbar: {
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
    loading?: boolean;
    onCancel?: () => void;
  }) => void;
  apiRef: React.RefObject<GridApiPremium>;
  getSelectedRows: () => any[];
  onBulkUpdate: (
    ids: (string | number)[],
    field: string,
    operations: BulkStringOperationConfig[]
  ) => Promise<{ success: boolean; error?: string }>;
  appendOutputLog?: (content: any) => void;
  setModalSwitchState?: (state: ModalSwitchState) => void;
}

export function InventoryTableButtonsPanel({
  rowSelectionModel,
  setSnackbar,
  apiRef,
  getSelectedRows,
  onBulkUpdate,
  appendOutputLog,
  setModalSwitchState,
}: InventoryTableButtonsPanelProps) {
  // State for dropdown menus
  const [supplierAnchorEl, setSupplierAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [workOrdersAnchorEl, setWorkOrdersAnchorEl] =
    useState<null | HTMLElement>(null);
  const [analysisAnchorEl, setAnalysisAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [warehouseAnchorEl, setWarehouseAnchorEl] =
    useState<null | HTMLElement>(null);
  const [curatorAnchorEl, setCuratorAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [openAnchorEl, setOpenAnchorEl] = useState<null | HTMLElement>(null);
  const [appendAnchorEl, setAppendAnchorEl] = useState<null | HTMLElement>(
    null
  );

  // State for directive builder controls
  const [houseDir, setHouseDir] = useState("H");
  const [store, setStore] = useState("Alliance");
  const [initials, setInitials] = useState("");

  // Selected DTBs can run for minutes on large selections (Amazon rate limits)
  const [dtbRunning, setDtbRunning] = useState(false);
  const dtbAbortRef = useRef<AbortController | null>(null);

  // Warn before the tab is closed mid-run
  useEffect(() => {
    if (!dtbRunning) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dtbRunning]);

  // Helper to check if rows are selected
  const hasSelection = rowSelectionModel.ids.size > 0;
  const hasSingleSelection = rowSelectionModel.ids.size === 1;

  // Discrete functions
  const calcAllDTBs = () => {
    if (window.confirm("Are you sure you want to recalculate all dtbs?")) {
      dvdlocator.post("/calc-all-dtbs");
    }
  };

  const calcSelectedDTBs = async (rows: any) => {
    if (rows.length === 0) {
      alert("Please select at least one row");
      return;
    }

    if (rows.some((row: any) => !row.supply.upc)) {
      alert("Please select only supply units.");
      return;
    }

    const upcs: string[] = rows.map((row: any) => row.supply.upc);

    const controller = new AbortController();
    dtbAbortRef.current = controller;
    setDtbRunning(true);

    const showProgress = (message: string, cancellable = true) =>
      setSnackbar({
        open: true,
        message,
        severity: "info",
        loading: true,
        onCancel: cancellable
          ? () => {
              controller.abort();
              showProgress("Cancelling after the current request...", false);
            }
          : undefined,
      });

    showProgress("Checking Amazon prices...");

    try {
      const summary = await calcSelectedDtbs(upcs, {
        post: (url: string, body: any) => dvdlocator.post(url, body),
        signal: controller.signal,
        confirmFetch: ({ asins, estimatedSeconds }: any) =>
          window.confirm(
            `${asins} products are missing an Amazon price. Fetching them is rate limited and will take about ${Math.ceil(
              estimatedSeconds / 60
            )} minute(s). Keep this tab open until it finishes (you can cancel at any time and resume later). Continue?`
          ),
        onProgress: ({ phase, done, total }: any) => {
          const step = Math.min(done + 1, total);
          if (phase === "checking") {
            showProgress("Checking Amazon prices...");
          } else if (phase === "fetching") {
            showProgress(`Fetching Amazon prices (${step}/${total})...`);
          } else {
            showProgress(`Calculating DTBs (${step}/${total})...`);
          }
        },
      });

      const { message, severity } = summarizeDtbRun(summary);
      setSnackbar({ open: true, message, severity });
    } catch (err) {
      console.log(err);
      setSnackbar({
        open: true,
        message: "Failed to calculate DTBs. Please try again.",
        severity: "error",
      });
    } finally {
      dtbAbortRef.current = null;
      setDtbRunning(false);
    }
  };

  const updateONO = async () => {
    try {
      await dvdlocator.post("/update-ono");
    } catch (err) {
      console.log(err);
    }
  };

  const updateDamageInventory = async () => {
    try {
      await dvdlocator.post("/inventory/damage/update-all");
    } catch (err) {
      console.log(err);
    }
  };

  // Menu handlers
  const handleSupplierClick = (event: MouseEvent<HTMLElement>) => {
    setSupplierAnchorEl(event.currentTarget);
  };
  const handleSupplierClose = () => {
    setSupplierAnchorEl(null);
  };

  const handleWorkOrdersClick = (event: MouseEvent<HTMLElement>) => {
    setWorkOrdersAnchorEl(event.currentTarget);
  };
  const handleWorkOrdersClose = () => {
    setWorkOrdersAnchorEl(null);
  };

  const handleAnalysisClick = (event: MouseEvent<HTMLElement>) => {
    setAnalysisAnchorEl(event.currentTarget);
  };
  const handleAnalysisClose = () => {
    setAnalysisAnchorEl(null);
  };

  const handleWarehouseClick = (event: MouseEvent<HTMLElement>) => {
    setWarehouseAnchorEl(event.currentTarget);
  };
  const handleWarehouseClose = () => {
    setWarehouseAnchorEl(null);
  };

  const handleCuratorClick = (event: MouseEvent<HTMLElement>) => {
    setCuratorAnchorEl(event.currentTarget);
  };
  const handleCuratorClose = () => {
    setCuratorAnchorEl(null);
  };

  const handleOpenClick = (event: MouseEvent<HTMLElement>) => {
    setOpenAnchorEl(event.currentTarget);
  };
  const handleOpenClose = () => {
    setOpenAnchorEl(null);
  };

  const handleAppendClick = (event: MouseEvent<HTMLElement>) => {
    setAppendAnchorEl(event.currentTarget);
  };
  const handleAppendClose = () => {
    setAppendAnchorEl(null);
  };

  // Button action handlers (all commented out for now)
  const handleDrctv = () => {
    const selectedRows = getSelectedRows();
    const content = directiveBuilder(selectedRows, houseDir, store, initials);
    if (content && appendOutputLog) {
      appendOutputLog(content);
    }
  };

  const handleSelectedDTBs = () => {
    const selectedRows = getSelectedRows();
    calcSelectedDTBs(selectedRows);
    handleSupplierClose();
  };

  const handleAllDTBs = () => {
    const accepted = window.confirm(
      `Are you sure you want to run the "All DTBs" task? It will reset all DTBs before running.`
    );
    if (!accepted) return;
    calcAllDTBs();
    handleSupplierClose();
  };

  const handleUploadSupplierData = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "UploadSupplierData",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "inventory",
      });
    }
    handleSupplierClose();
  };

  const handleClearSupplierData = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "ClearSupplierData",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "inventory",
      });
    }
    handleSupplierClose();
  };

  const handleUpdateONO = () => {
    updateONO();
    handleSupplierClose();
  };

  const handleDamageWorkOrder = () => {
    const selectedRows = getSelectedRows();
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "DamageWorkOrder",
        selectedRows: selectedRows,
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "inventory",
      });
    }
    handleWorkOrdersClose();
  };

  const handleManualUpdateDMGInv = () => {
    updateDamageInventory();
    handleWorkOrdersClose();
  };

  const handleUPCs = () => {
    const selectedRows = getSelectedRows();
    console.log(selectedRows);
    const upcs: string[] = [];
    selectedRows.forEach((row: any) => {
      upcs.push(row["supply"]["upc"]);
    });
    if (upcs.length) {
      if (setModalSwitchState) {
        setModalSwitchState({
          modalName: "MessageModal",
          selectedRows: [],
          appendStatus: false,
          appendErrors: [],
          columnNames: [],
          mode: "inventory",
          message: upcs.join(","),
          messageType: "upc",
          messageCopy: true,
        });
      }
    } else {
      alert("Please select row(s)");
    }
    handleAnalysisClose();
  };

  const handleSumColumn = () => {
    const selectedRows = getSelectedRows();
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "SumColumn",
        selectedRows: selectedRows,
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "inventory",
      });
    }
    handleAnalysisClose();
  };

  const handleRefreshAnalyticsColumns = () => {
    dvdlocator
      .post("/refresh-analytics-db-columns")
      .then((res) => {
        setSnackbar({
          open: true,
          message:
            "Successfully started task! Please wait a moment for update.",
          severity: "success",
        });
      })
      .catch((err) => {
        setSnackbar({
          open: true,
          message: "Error starting task.",
          severity: "error",
        });
      });
    handleAnalysisClose();
  };

  const handleUPCBarcode = () => {
    const selectedRows = getSelectedRows();

    if (selectedRows.length === 0) {
      alert("Please select at least one row");
      handleWarehouseClose();
      return;
    }

    let rowData = selectedRows;

    if (selectedRows.length === 1) {
      const count = prompt(
        "How many copies of this barcode would you like?",
        "1"
      );
      const numCopies = parseInt(count || "1", 10);
      rowData = Array(numCopies).fill(selectedRows[0]);
    }

    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "BarcodeModal",
        selectedRows: selectedRows,
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "inventory",
        barCodeType: "upc",
        rowData: rowData,
      });
    }
    handleWarehouseClose();
  };

  const handleGenerateSlotLabel = () => {
    const selectedRows = getSelectedRows();

    if (selectedRows.length === 0) {
      alert("Please select at least one row");
      handleWarehouseClose();
      return;
    }

    let rowData = [];

    if (selectedRows.length === 1) {
      const count = prompt(
        "How many copies of this label would you like?",
        "1"
      );
      const numCopies = parseInt(count || "1", 10);

      for (let i = 0; i < numCopies; i++) {
        rowData.push(selectedRows[0]);
      }
    } else {
      rowData = selectedRows;
    }

    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "SlotLabelModal",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "inventory",
        rowData: rowData,
        slotLabelType: "inventory",
      });
    }
    handleWarehouseClose();
  };

  const handleGenerateShelvingList = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "ShelvingList",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "inventory",
      });
    }
    handleWarehouseClose();
  };

  const handleClearSortColumn = async () => {
    if (
      !window.confirm(
        "Are you sure you would like to clear the data in the Sort column?"
      )
    ) {
      handleCuratorClose();
      return;
    }

    const selectedRows = getSelectedRows();

    if (selectedRows.length === 0) {
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: "Please select at least one row",
          severity: "error",
        });
      }
      handleCuratorClose();
      return;
    }

    try {
      for (const row of selectedRows) {
        const updatedRow = _.cloneDeep(row);

        // Update using dot-notation format for proper change detection
        updatedRow["supply.sort"] = "";

        // Update the row via the dataSource to send server update
        await apiRef.current.dataSource.editRow({
          rowId: row.id,
          updatedRow: updatedRow,
          previousRow: row,
        });
      }

      // Success notification handled by dataSource.editRow
    } catch (error) {
      console.error("Error clearing Sort column:", error);
      // Error notification already handled by dataSource.editRow
    }

    handleCuratorClose();
  };

  const handleProductFamilies = async () => {
    const selectedRows = getSelectedRows();
    const colset = new URLSearchParams(window.location.search).get("colset");

    for (const row of selectedRows) {
      if (
        row.supply_families_view?.families &&
        row.supply_families_view.families.length > 0
      ) {
        const families = row.supply_families_view.families
          .split(",")
          .map((fam: string) => fam.trim());

        for (const family of families) {
          await new Promise((r) => setTimeout(r, 500));
          if (family !== "") {
            const params = new URLSearchParams();
            params.append("tab", "all");
            if (colset) params.set("colset", colset);
            params.set(
              "listings_filter_product_families_view.families_operator",
              "contains"
            );
            params.set(
              "listings_filter_product_families_view.families_value",
              family
            );

            params.set(
              "inventory_filter_supply_families_view.families_operator",
              "contains"
            );
            params.set(
              "inventory_filter_supply_families_view.families_value",
              family
            );

            const url = `/datatables/pricetool?${params.toString()}`;
            window.open(url, "_blank");
          }
        }
      }
    }
    handleOpenClose();
  };

  const handleGmail = () => {
    const selectedRows = getSelectedRows();

    selectedRows.forEach((row: any) => {
      const upc = row.supply?.upc;
      if (upc) {
        window.open(
          `https://mail.google.com/mail/u/0/#search/${upc}`,
          "_blank"
        );
      }
    });
    handleOpenClose();
  };

  const handleBuyBox = () => {
    const selectedRows = getSelectedRows();

    selectedRows.forEach((row: any) => {
      const asin = row.supply?.supply_asin;
      if (asin) {
        window.open(`https://www.amazon.com/dp/${asin}`, "_blank");
      }
    });
    handleOpenClose();
  };

  const handleHouse = () => {
    const selectedRows = getSelectedRows();

    selectedRows.forEach((row: any) => {
      const upc = row.supply?.upc;
      if (upc) {
        const params = new URLSearchParams();
        params.set("house_search", upc);
        params.set("house_filter_house.upc_operator", "equals");
        params.set("house_filter_house.upc_value", upc);
        window.open(`/datatables/house?${params.toString()}`, "_blank");
      }
    });
    handleOpenClose();
  };

  const handleHouseReceived = () => {
    const selectedRows = getSelectedRows();

    selectedRows.forEach((row: any) => {
      const upc = row.supply?.upc;
      if (upc) {
        const params = new URLSearchParams();
        params.set("house_search", upc);
        params.set("house_filter_house.upc_operator", "equals");
        params.set("house_filter_house.upc_value", upc);
        window.open(
          `/datatables/house-received?${params.toString()}`,
          "_blank"
        );
      }
    });
    handleOpenClose();
  };

  const handleDamages = () => {
    const selectedRows = getSelectedRows();

    selectedRows.forEach((row: any) => {
      const upc = row.supply?.upc;
      if (upc) {
        const params = new URLSearchParams();
        params.set("damage_search", upc);
        params.set("damage_filter_damage.upc_operator", "equals");
        params.set("damage_filter_damage.upc_value", upc);
        window.open(`/datatables/damage?${params.toString()}`, "_blank");
      }
    });
    handleOpenClose();
  };

  const handlePrependListed = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "supply.listed", type: "string" }],
        mode: "inventory",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependHouse = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "supply.house", type: "string" }],
        mode: "inventory",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependHouse2 = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "supply.house_2", type: "string" }],
        mode: "inventory",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependInventoryNotes = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "supply.inventory_notes", type: "string" }],
        mode: "inventory",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependThreadingNotes = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "supply.threading_notes", type: "string" }],
        mode: "inventory",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const storeOptions = [
    "Alliance",
    "Amazon",
    "AmazonCa",
    "AmazonFBA",
    "AmazonUK",
    "Barnes&Noble",
    "BestBuy",
    "Blowit",
    "Blowit Ebay",
    "BookDepository",
    "BullMoose",
    "CCVideo",
    "DealsAreUs",
    "DeepDiscount",
    "Discount Entertainment",
    "Ebay",
    "Gruv",
    "InetVideo",
    "Mila",
    "PBS",
    "Rarewaves",
    "Rightstuff",
    "Scholastic",
    "Target",
    "UNV Amazon",
    "UNV Ebay",
    "UNV Half",
    "VEI",
    "Walmart",
    "Wildcard1",
    "Wildcard2",
    "Wildcard3",
    "Wildcard4",
    "WowHD",
    "UA11",
  ];

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        padding: 2,
        borderBottom: "1px solid #e0e0e0",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {/* Directive Builder Controls */}
      <FormControl size="small" sx={{ minWidth: 50 }}>
        <Select
          value={houseDir}
          onChange={(e) => setHouseDir(e.target.value)}
          sx={{ fontSize: "10px", height: "100%" }}
        >
          <MenuItem value="H">H</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select
          value={store}
          onChange={(e) => setStore(e.target.value)}
          sx={{ fontSize: "10px", height: "100%" }}
        >
          {storeOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        placeholder="Init."
        value={initials}
        onChange={(e) => setInitials(e.target.value)}
        sx={{
          minWidth: "60px",
          "& input": { fontSize: "9px", padding: "4px 8px" },
        }}
      />

      {/* Drctv Button */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleDrctv}
        title="Create Directive"
      >
        Drctv
      </Button>

      {/* Supplier Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleSupplierClick}
        endIcon={<ArrowDropDown />}
      >
        Supplier
      </Button>
      <Menu
        anchorEl={supplierAnchorEl}
        open={Boolean(supplierAnchorEl)}
        onClose={handleSupplierClose}
      >
        <MenuItem onClick={handleSelectedDTBs} disabled={dtbRunning}>
          Selected DTBs
        </MenuItem>
        <MenuItem onClick={handleAllDTBs}>All DTBs</MenuItem>
        <MenuItem
          onClick={handleUploadSupplierData}
          title="Upload CSV pricing/inventory data from limited set of suppliers"
        >
          Upload Supplier Data
        </MenuItem>
        <MenuItem
          onClick={handleClearSupplierData}
          title="Clear out prices for selected suppliers"
        >
          Clear Supplier Data
        </MenuItem>
        <MenuItem onClick={handleUpdateONO}>Update ONO</MenuItem>
      </Menu>

      {/* Work Orders Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleWorkOrdersClick}
        endIcon={<ArrowDropDown />}
      >
        Work Orders
      </Button>
      <Menu
        anchorEl={workOrdersAnchorEl}
        open={Boolean(workOrdersAnchorEl)}
        onClose={handleWorkOrdersClose}
      >
        <MenuItem
          onClick={handleDamageWorkOrder}
          title="Used by an Analyst to mark up a product with a work order for warehouse personnel. Repair means we are going to fix the product in house, Return means it will be going back to the supplier."
        >
          Damage Work Order
        </MenuItem>
        <MenuItem
          onClick={handleManualUpdateDMGInv}
          title="Used when making manual edits to the Damage Datatable - updates the DMG_INV column to reflect these edits."
        >
          Manual Update DMG Inv
        </MenuItem>
      </Menu>

      {/* Analysis Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleAnalysisClick}
        title="Analyze Item"
        endIcon={<ArrowDropDown />}
      >
        Analysis
      </Button>
      <Menu
        anchorEl={analysisAnchorEl}
        open={Boolean(analysisAnchorEl)}
        onClose={handleAnalysisClose}
      >
        <MenuItem onClick={handleUPCs} title="Copy selected UPCs">
          UPCs
        </MenuItem>
        <MenuItem
          onClick={handleSumColumn}
          title="Sum up the values of a specific column for selected rows."
        >
          Sum Column
        </MenuItem>
        <MenuItem onClick={handleRefreshAnalyticsColumns}>
          Refresh Analytics Columns
        </MenuItem>
      </Menu>

      {/* Warehouse Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleWarehouseClick}
        title="Ware House Actions"
        endIcon={<ArrowDropDown />}
      >
        Warehouse
      </Button>
      <Menu
        anchorEl={warehouseAnchorEl}
        open={Boolean(warehouseAnchorEl)}
        onClose={handleWarehouseClose}
      >
        <MenuItem
          onClick={handleUPCBarcode}
          disabled={!hasSelection}
          title="Bar Code"
        >
          UPC Barcode
        </MenuItem>
        <MenuItem
          onClick={handleGenerateSlotLabel}
          disabled={!hasSelection}
          title="Generate warehouse shelving slot label for selected row(s)"
        >
          Generate Slot Label
        </MenuItem>
        <MenuItem onClick={handleGenerateShelvingList}>
          Generate Shelving List
        </MenuItem>
      </Menu>

      {/* Curator Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleCuratorClick}
        title="Jons Precious Menu"
        endIcon={<ArrowDropDown />}
      >
        Curator
      </Button>
      <Menu
        anchorEl={curatorAnchorEl}
        open={Boolean(curatorAnchorEl)}
        onClose={handleCuratorClose}
      >
        <MenuItem onClick={handleClearSortColumn} title="Clear Sort Column ">
          Clear "Sort" Column
        </MenuItem>
      </Menu>

      {/* Open Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpenClick}
        title="Search for Items"
        endIcon={<ArrowDropDown />}
      >
        Open
      </Button>
      <Menu
        anchorEl={openAnchorEl}
        open={Boolean(openAnchorEl)}
        onClose={handleOpenClose}
      >
        <MenuItem onClick={handleProductFamilies} title="Open Families">
          Product Families
        </MenuItem>
        <MenuItem onClick={handleGmail} title="Open UPC in Gmail">
          Gmail
        </MenuItem>
        <MenuItem onClick={handleBuyBox} title="Open ASIN in Buy Box">
          Buy Box
        </MenuItem>
        <MenuItem onClick={handleHouse} title="Open UPC in House Ordered table">
          House
        </MenuItem>
        <MenuItem
          onClick={handleHouseReceived}
          title="Open UPC in House Received table"
        >
          House Received
        </MenuItem>
        <MenuItem onClick={handleDamages} title="Open UPC in Damaged table">
          Damages
        </MenuItem>
      </Menu>

      {/* Prepend Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleAppendClick}
        title="Prepend to Field"
        endIcon={<ArrowDropDown />}
      >
        Prepend
      </Button>
      <Menu
        anchorEl={appendAnchorEl}
        open={Boolean(appendAnchorEl)}
        onClose={handleAppendClose}
      >
        <MenuItem
          onClick={handlePrependListed}
          title="Prepend / Remove from Listed"
        >
          Listed
        </MenuItem>
        <MenuItem
          onClick={handlePrependHouse}
          title="Prepend / Remove from House"
        >
          House
        </MenuItem>
        <MenuItem
          onClick={handlePrependHouse2}
          title="Prepend / Remove from House 2"
        >
          House 2
        </MenuItem>
        <MenuItem
          onClick={handlePrependInventoryNotes}
          title="Prepend / Remove from Inventory Notes"
        >
          Inventory Notes
        </MenuItem>
        <MenuItem
          onClick={handlePrependThreadingNotes}
          title="Prepend / Remove from Threading Notes"
        >
          Threading Notes
        </MenuItem>
      </Menu>
    </Box>
  );
}
